import type { IChannelState } from 'src/types/checkout';

export const CHECKOUT_STORAGE_KEY = 'app-channel';

export const CHANNEL_STEPS = (t: any, channelType: string, emailProviderType: string) => {
  const isEmailChannel = channelType === 'EMAIL';
  const isSMSChannel = channelType === 'SMS';
  const isLineChannel = channelType === 'LINE';
  const isOtherProvider = emailProviderType === 'other_provider';

  const stepsList = {
    chooseProvider: {
      id: 'choose-provider',
      header: t('CHOOSE_PROVIDER'),
      description: t('CHOOSE_PROVIDER_DESCRIPTION'),
    },
    chooseEmailProvider: {
      id: 'choose-email-provider',
      header: t('CHOOSE_EMAIL_PROVIDER'),
      description: t('CHOOSE_EMAIL_PROVIDER_DESCRIPTION'),
    },
    chooseSmsProvider: {
      id: 'choose-sms-provider',
      header: t('CHOOSE_SMS_PROVIDER'),
      description: t('CHOOSE_SMS_PROVIDER_DESCRIPTION'),
    },
    lineSetupRequirements: {
      id: 'line-setup-requirements',
      header: t('LINE_CHANNEL_SETUP'),
      description: t('LINE_CHANNEL_SETUP_DESCRIPTION'),
    },
    createChannel: {
      id: 'create-channel',
      header: t('CREATE_CHANNEL'),
      description: t('CREATE_CHANNEL_DESCRIPTION'),
    },
    smtpConfig: {
      id: 'smtp-config',
      header: t('SMTP_CONFIGURATION'),
      description: t('SMTP_DESCRIPTION'),
    },
    imapConfig: {
      id: 'imap-config',
      header: t('IMAP_CONFIGURATION'),
      description: t('IMAP_DESCRIPTION'),
    },
    addUsers: {
      id: 'add-users',
      header: t('ADD_USER_AND_TEAMS'),
      description: t('ADD_USERS_DESCRIPTION'),
    },
    finish: {
      id: 'finish',
      header: t('FINISH'),
      description: t('FINISH_DESCRIPTION'),
    },
  };

  // EMAIL – Other Provider (7 steps)
  if (isEmailChannel && isOtherProvider) {
    return [
      stepsList.chooseProvider,
      stepsList.chooseEmailProvider,
      stepsList.createChannel,
      stepsList.smtpConfig,
      stepsList.imapConfig,
      stepsList.addUsers,
      stepsList.finish,
    ];
  }

  // EMAIL – Google / Microsoft (5 steps)
  if (isEmailChannel) {
    return [
      stepsList.chooseProvider,
      stepsList.chooseEmailProvider,
      stepsList.createChannel,
      stepsList.addUsers,
      stepsList.finish,
    ];
  }

  // LINE (4 steps)
  if (isLineChannel) {
    return [
      stepsList.chooseProvider,
      stepsList.lineSetupRequirements,
      stepsList.createChannel,
      stepsList.finish,
    ];
  }

  // Default fallback (safe)
  return [stepsList.chooseProvider, stepsList.createChannel, stepsList.addUsers, stepsList.finish];
};

export const initialState: IChannelState = {
  channelType: '',
  channelName: '',
  whatsappPin: '',
  assignedType: '',
  assignUsers: [],
  facebookDetails: { main_token: '' },
  lineDetails: {
    lineChannelName: '',
    lineChannelSecret: '',
    lineChannelId: '',
    channelCreated: false,
  },
  emailProviderType: '',
  SMSProviderType: 'bms_api',
  telegramBotToken: '',
  emailChannelDtls: { channelName: '', email: '' },
  googleEmailDetails: { code: '' },
  microsoftEmailDetails: { code: '' },
  microsoftTeamsDetails: { code: '' },
  LiveChatDeatil: {
    websiteDomin: '',
    websiteName: '',
    widgetColor: '',
    welcomeTitle: '',
    welcomeTagline: '',
  },
  smtpDetails: {
    smtpEnabled: true,
    smtpAddress: '',
    smtpPort: 0,
    smtpLogin: '',
    smtpPassword: '',
    smtpDomain: '',
  },
  imapDetails: {
    imapEnabled: true,
    imapAddress: '',
    imapPort: 0,
    imapLogin: '',
    imapPassword: '',
    imapEnableSsl: true,
  },
  voiceCallDetails: { assignUsers: [], assignedType: '', didNum: '', didType: '' },
  channelCreatedResponse: null,
  smsChannelDtls: { accountSid: '', authToken: '', srcNumber: '' },
};

export const getAssignedPayload = (assignUsers?: any[]) => {
  const assignedUserIds: string[] = [];
  const assignedTeamIds: string[] = [];
  const assignedTypes: string[] = [];

  assignUsers?.forEach((u) => {
    if (u.assignedType === 'USER') assignedUserIds.push(u.id);
    if (u.assignedType === 'TEAM') assignedTeamIds.push(u.id);
  });

  if (assignedUserIds.length) assignedTypes.push('USER');
  if (assignedTeamIds.length) assignedTypes.push('TEAM');

  return {
    assignedType: assignedTypes,
    assignedUser: assignedUserIds,
    assignedTeam: assignedTeamIds,
  };
};

export const ERROR_MAP: Record<string, string> = {
  'channelName already exists': 'DUPLICATE_CHANNEL',
  'phoneNumber already exists': 'DUPLICATE_NUMBER',
  'No matching DID available in stock for any eligible subscription.': 'NO_MATCHING_DID_IN_STOCK',
  'No available DIDs to assign: All tags in your current packages are already used. Please add new AddOn for that.':
    'NO_AVAILABLE_DIDS_TO_ASSIGN',
  'You have already created a DID number based on your current package. Please extend your package limit to add more.':
    'DID_LIMIT_REACHED',
};
